import { atom } from "jotai";
import { atomWithStorage, createJSONStorage } from "jotai/utils";
import type { SetStateAction } from "react";
import {
  collapsedForGenieAtom,
  sidenavRevealAtom,
} from "@/components/Spotflow/SideNav/atoms";

// A Genie session belongs to one browser tab: a reload restores it, a new tab
// starts closed with its own chat, and no tab is ever opened by another. The
// atoms read storage at module init, so the server gets an empty stand-in.
const serverStorage = {
  getItem: () => null,
  setItem: () => {},
  removeItem: () => {},
};
const tabStringStorage = () =>
  typeof window === "undefined" ? serverStorage : window.sessionStorage;
const tabStorage = <T>() => createJSONStorage<T>(tabStringStorage);

const genieOpenStorageAtom = atomWithStorage(
  "genie-open",
  false,
  tabStorage<boolean>(),
  { getOnInit: true }
);

// Opening Genie collapses this tab's sidenav to make room; closing it expands
// the sidenav again, but only when it was pinned before Genie. The user's pin
// itself is never written.
export const genieOpenAtom = atom(
  (get) => get(genieOpenStorageAtom),
  (get, set, value: SetStateAction<boolean>) => {
    const isOpen = get(genieOpenStorageAtom);
    const next = typeof value === "function" ? value(isOpen) : value;
    if (next === isOpen) return;
    set(genieOpenStorageAtom, next);
    if (next) set(sidenavRevealAtom, false);
    set(collapsedForGenieAtom, next);
  }
);

export const geniePanelExitedAtom = atom(true);

export const genieConversationByTenantAtom = atomWithStorage<
  Record<string, string>
>("genie-conversation-by-tenant", {}, tabStorage(), { getOnInit: true });

// The one element Genie points the user at, its note, and the page it was
// made on (highlight_element).
export interface GenieSpotlight {
  target: HTMLElement;
  note: string;
  pathname: string;
}

export const genieSpotlightAtom = atom<GenieSpotlight | null>(null);

// A request a page puts in Genie's composer for the user to send.
export const genieDraftAtom = atom<string | null>(null);

export const genieDebugLandmarksAtom = atom(false);

export const geniePanelWidthAtom = atomWithStorage(
  "genie-panel-width",
  360,
  undefined,
  { getOnInit: true }
);
